import { Box, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useAppSelector } from '../../../app/hooks';

import { useDeleteRoleMutation } from '../../../services/entities/impl/blfApi/endpoints/permissions';
import { useDeleteUserMutation } from '../../../services/entities/impl/blfApi/endpoints/users';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import {
  Customer,
  CustomerSchema,
} from '../../../services/entities/impl/blfApi/schemas/customer';

import {
  Ranch,
  RanchSchema,
} from '../../../services/entities/impl/blfApi/schemas/ranch';
import {
  RoleSchema,
  User,
  UserSchema,
} from '../../../services/entities/impl/blfApi/schemas/user';
import { useFeatureFlags } from '../../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import LoadingSkeletonGeneric from '../../commonUI/LoadingSkeletonGeneric';
import { capitalizeFirstLettersOnly, validateWithYup } from '../../commonUtils';
import { roundFractional } from '../../ranches/ranchUtils';
import { useOnboardContext } from '../context/OnboardContext';
import { BlockValidationSchema } from '../forms/schemas/block';
import {
  OnboardingTabsEnum,
  selectCurrOnboardingTab,
  selectInvalidItemsCheckbox,
  selectOnboardSearch,
} from '../onboardingSlice';
import {
  SendStatus,
  SendStatusEnum,
  initialSendStatus,
  shouldShowItem,
  validateOptionalFieldsBlock,
} from '../onboardingUtils';
import OnboardListHeader from './OnboardListHeader';
import {
  ItemIDs,
  OnboardListItemParams,
  OnboardSubtitle,
} from './OnboardListItem';
import { OnboardVirtualizedList } from './OnboardVirtualizedList';

const OnboardList: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currTab = useAppSelector(selectCurrOnboardingTab);
  const showInvalidOnly = useAppSelector(selectInvalidItemsCheckbox);
  const searchVal = useAppSelector(selectOnboardSearch);
  const { ApiDataRaw, ApiDataFiltered } = useOnboardContext();
  const [deleteUser] = useDeleteUserMutation();
  const [deleteRole] = useDeleteRoleMutation();

  const { Diagnostics } = useFeatureFlags();

  const [deleteStatus, setDeleteStatus] = useState<SendStatus>({
    ...initialSendStatus,
  });

  const handleDeleteUser = async (id: string) => {
    try {
      if (id) {
        setDeleteStatus({
          status: SendStatusEnum.LOADING,
          message: '',
        });
        // get user to delete
        const userResult = ApiDataFiltered.users?.find(
          (user) => user.id === id
        );

        if (!userResult) {
          setDeleteStatus({
            status: SendStatusEnum.FAILED,
            message: 'User not found.',
          });
          throw new Error('User not found.');
        }

        // loop through roles and delete each one
        if (userResult.roles) {
          for (const role of userResult.roles) {
            if (role.id) {
              console.log('Deleting role: ', role);
              await deleteRole(role.id as number);
            }
          }
        }
        // delete user
        await deleteUser(id);
        console.log('User deleted successfully.');
        setDeleteStatus({
          status: SendStatusEnum.SUCCESS,
          message: 'User deleted successfully.',
        });
      }
    } catch (e) {
      setDeleteStatus({
        status: SendStatusEnum.FAILED,
        message: 'Failed to delete user, see console for details.',
      });
      alert('Failed to delete user, see console for details.');
      console.log(e);
    }
  };

  if (deleteStatus.status === SendStatusEnum.FAILED) {
    console.log('Delete status: ', deleteStatus);
    console.log('avoiding unused variable warning', handleDeleteUser); // delete me
  }

  const createCustomersList = (
    customers: Customer[],
    showInvalidOnly: boolean,
    searchVal: string
  ) => {
    const items: OnboardListItemParams[] = [];

    customers.forEach((customer) => {
      const { errors } = validateWithYup(CustomerSchema, customer);

      const shouldShow = shouldShowItem({
        searchVal: searchVal,
        searchObject: customer,
        searchableFields: ['id', 'name', 'unitDisplay', 'shortCode'],
        showInvalidOnly: showInvalidOnly,
        errors: errors,
      });

      if (!shouldShow) {
        return;
      }

      const subTitles: OnboardSubtitle[] = [];

      subTitles.push({
        title: t('Units', { ns: 'common' }),
        text: customer.unitDisplay,
      });

      subTitles.push({
        title: t('Short Code', { ns: 'common' }),
        text: customer.shortCode,
        hidden: !Diagnostics,
      });

      const itemIDs: ItemIDs = {
        customerID: customer.id,
        ranchID: '',
        blockID: '',
        userID: '',
      };

      items.push({
        id: customer.id,
        title: customer.name,
        itemIDs,
        subTitles: subTitles,
        errors: errors,
      });
    });
    return items;
  };

  const createRanchesList = (
    customers: Customer[],
    ranches: Ranch[],
    showInvalidOnly: boolean,
    searchVal: string
  ) => {
    const items: OnboardListItemParams[] = [];

    ranches.forEach((ranch) => {
      const relatedCustomer = customers.find(
        (customer) => customer.id === ranch.customerID
      );
      const customerName = relatedCustomer ? relatedCustomer.name : '';

      const missingParentErrors: string[] = [];
      if (!relatedCustomer) {
        missingParentErrors.push(
          `${t('Missing', { ns: 'common' })} ${t('Customer', { ns: 'common' })}`
        );
      }

      const { errors } = validateWithYup(RanchSchema, ranch);

      const combinedErrors = [...errors, ...missingParentErrors];

      const shouldShow = shouldShowItem({
        searchVal: searchVal,
        searchObject: ranch,
        searchableFields: ['id', 'customerID', 'name', 'region'],
        showInvalidOnly: showInvalidOnly,
        errors: combinedErrors,
        additionalSearchItems: [customerName],
      });

      if (!shouldShow) {
        return;
      }

      const subTitles: OnboardSubtitle[] = [];

      subTitles.push({
        title: t('Customer', { ns: 'common' }),
        text: customerName,
      });

      subTitles.push({
        title: t('Region', { ns: 'common' }),
        text: ranch.region,
      });

      const itemIDs: ItemIDs = {
        customerID: relatedCustomer ? relatedCustomer.id : '',
        ranchID: ranch.id,
        blockID: '',
        userID: '',
      };

      items.push({
        id: ranch.id,
        title: ranch.name,
        itemIDs,
        subTitles: subTitles,
        errors: errors,
      });
    });
    return items;
  };

  const createBlocksList = (
    customers: Customer[],
    ranches: Ranch[],
    blocks: Block[],
    showInvalidOnly: boolean,
    searchVal: string
  ): OnboardListItemParams[] => {
    const items: OnboardListItemParams[] = [];

    blocks.forEach((block) => {
      const relatedRanch = ranches.find((ranch) => ranch.id === block.ranchID);

      const ranchName = relatedRanch ? relatedRanch.name : '';

      const relatedCustomer = customers.find(
        (customer) => customer.id === relatedRanch?.customerID
      );

      const customerName = relatedCustomer ? relatedCustomer.name : '';

      const missingParentErrors: string[] = [];
      if (!relatedRanch) {
        missingParentErrors.push(
          `${t('Missing', { ns: 'common' })} ${t('Ranch', { ns: 'common' })}`
        );
      }
      if (!relatedCustomer) {
        missingParentErrors.push(
          `${t('Missing', { ns: 'common' })} ${t('Customer', { ns: 'common' })}`
        );
      }

      const { errors } = validateWithYup(BlockValidationSchema, block);
      const {
        errors: optionalErrors,
        warnings,
        acresMismatch,
        acresResult,
      } = validateOptionalFieldsBlock(block);

      const combinedErrors = [
        ...errors,
        ...optionalErrors,
        ...missingParentErrors,
      ];

      const shouldShow = shouldShowItem({
        searchVal: searchVal,
        searchObject: block,
        searchableFields: [
          'id',
          'ranchID',
          'name',
          'trellisSystem',
          'pruningMethod',
          'rootStock',
        ],
        showInvalidOnly: showInvalidOnly,
        errors: combinedErrors,
        warnings: warnings,
        additionalSearchItems: relatedCustomer
          ? [customerName, relatedCustomer.id, relatedCustomer.shortCode]
          : [],
      });

      if (!shouldShow) {
        return;
      }

      const subTitles: OnboardSubtitle[] = [];

      subTitles.push({
        title: t('Customer', { ns: 'common' }),
        text: customerName,
      });

      subTitles.push({
        title: t('Ranch', { ns: 'common' }),
        text: ranchName,
      });

      // TODO: Refactor OnboardList structure to support fetching variety for each block using useVariety(block.varietyID)
      // Currently using deprecated block.variety?.cropType as a temporary solution
      // since we can't fetch all varieties without customer context in this component structure
      const cropTypeText = block.variety?.cropType || 'Unknown';
      subTitles.push({
        title: t('Crop Type', { ns: 'common' }),
        text: cropTypeText,
      });

      let acresText = block.acres
        ? roundFractional(block.acres, 1).toString()
        : null;
      if (acresMismatch && acresResult.calculatedAcres) {
        acresText = `${
          block.acres ? roundFractional(block.acres, 1).toString() : 'N/A'
        } - Calculated: ${roundFractional(
          acresResult.calculatedAcres,
          1
        ).toString()}`;
      }

      subTitles.push({
        title: t('Acres', { ns: 'common' }),
        text: acresText,
        hasError: acresMismatch,
      });

      subTitles.push({
        title: `${t('Plant Spacing', { ns: 'common' })} (ft)`,
        text: block.plantSpacing ? block.plantSpacing.toString() : '',
      });

      subTitles.push({
        title: `${t('Row Spacing', { ns: 'common' })} (ft)`,
        text: block.rowSpacing ? block.rowSpacing.toString() : '',
      });

      const itemIDs: ItemIDs = {
        customerID: relatedCustomer ? relatedCustomer.id : '',
        ranchID: relatedRanch ? relatedRanch.id : '',
        blockID: block.id,
        userID: '',
      };

      items.push({
        id: block.id,
        title: block.name,
        itemIDs,
        subTitles: subTitles,
        warnings: warnings,
        errors: combinedErrors,
      });
    });
    return items;
  };

  const createUsersList = (
    customers: Customer[],
    users: User[],
    showInvalidOnly: boolean,
    searchVal: string
  ) => {
    const items: OnboardListItemParams[] = [];

    users.forEach((user) => {
      const missingParentErrors: string[] = [];
      const additionalSearchItems: string[] = [];

      const relatedCustomer = customers.find(
        (customer) => customer.id === user.customerID
      );

      const customerName = relatedCustomer ? relatedCustomer.name : '';

      if (relatedCustomer) {
        additionalSearchItems.push(relatedCustomer.name);
      }

      // Loop through each user's roles, check for errors, and create a list of roles and searchable fields from the related customers
      const rolesList: OnboardSubtitle[] = user.roles.map((role) => {
        // find the customer name
        let customerNameRole = role.resourceID === '*' ? 'All Customers' : '';
        // Make "All Customers" searchable for the role "*" since this is a special case and is overwritten with bloomfield later
        if (role.resourceID === '*') {
          additionalSearchItems.push('All Customers');
        }

        // Find the related customer for the role
        if (customers) {
          const relatedCustomerForRole = customers.find(
            (customer) => customer.id === role.resourceID
          );

          if (relatedCustomerForRole) {
            customerNameRole = relatedCustomerForRole.name;
            // Add searchable fields from the related customers
            additionalSearchItems.push(customerNameRole);
            additionalSearchItems.push(relatedCustomerForRole.id);
            additionalSearchItems.push(relatedCustomerForRole.shortCode);
          }

          additionalSearchItems.push(role.name);

          // Check for missing customer
          if (role.resourceID !== '*' && !relatedCustomerForRole) {
            missingParentErrors.push(
              `${t('Missing', { ns: 'common' })} ${t('Customer', {
                ns: 'common',
              })}`
            );
          }
        }

        return {
          title: capitalizeFirstLettersOnly(role.name),
          text: customerNameRole,
        };
      });

      const { errors: errorsUser } = validateWithYup(UserSchema, user);

      // Check all users' roles for errors
      const errorsRoles: string[] = [];
      user.roles.forEach((role) => {
        const { errors } = validateWithYup(RoleSchema, role);
        errorsRoles.push(...errors);
      });

      const combinedErrors = [
        ...errorsUser,
        ...errorsRoles,
        ...missingParentErrors,
      ];

      const shouldShow = shouldShowItem({
        searchVal: searchVal,
        searchObject: user,
        searchableFields: [
          'id',
          'customerID',
          'firstName',
          'lastName',
          'email',
        ],
        showInvalidOnly: showInvalidOnly,
        errors: combinedErrors,
        additionalSearchItems: additionalSearchItems,
      });

      if (!shouldShow) {
        return;
      }

      const subTitles: OnboardSubtitle[] = [];

      subTitles.push({
        title: t('Customer', { ns: 'common' }),
        text: customerName,
      });

      subTitles.push({
        title: t('Email', { ns: 'common' }),
        text: user.email,
      });

      const itemIDs: ItemIDs = {
        customerID: relatedCustomer ? relatedCustomer.id : '',
        ranchID: '',
        blockID: '',
        userID: user.id,
      };

      items.push({
        id: user.id,
        title: `${user.firstName} ${user.lastName}`,
        itemIDs,
        subTitles: [...subTitles, ...rolesList],
        errors: combinedErrors,
      });
    });
    return items;
  };

  // Create the list items
  const listItems = useMemo(() => {
    let items: OnboardListItemParams[] = [];
    switch (currTab) {
      case OnboardingTabsEnum.customers:
        items = createCustomersList(
          ApiDataFiltered.customers,
          showInvalidOnly,
          searchVal
        );
        break;
      case OnboardingTabsEnum.ranches:
        items = createRanchesList(
          ApiDataFiltered.customers,
          ApiDataFiltered.ranches,
          showInvalidOnly,
          searchVal
        );
        break;
      case OnboardingTabsEnum.blocks:
        items = createBlocksList(
          ApiDataFiltered.customers,
          ApiDataFiltered.ranches,
          ApiDataFiltered.blocks,
          showInvalidOnly,
          searchVal
        );
        break;
      case OnboardingTabsEnum.users:
        items = createUsersList(
          ApiDataRaw.customers ? ApiDataRaw.customers : [],
          ApiDataFiltered.users,
          showInvalidOnly,
          searchVal
        );
        break;
      default:
        items = [];
    }
    return items;
  }, [ApiDataFiltered, ApiDataRaw, currTab, showInvalidOnly, searchVal]);

  // Get loading status and counts
  const visibleTotal = listItems.length;
  let isLoading = false;
  let total = 0;
  switch (currTab) {
    case OnboardingTabsEnum.customers:
      isLoading = ApiDataRaw.isLoadingCustomers;
      total = ApiDataRaw.customers?.length || 0;
      break;
    case OnboardingTabsEnum.ranches:
      isLoading = ApiDataRaw.isLoadingRanches;
      total = ApiDataRaw.ranches?.length || 0;
      break;
    case OnboardingTabsEnum.blocks:
      isLoading = ApiDataRaw.isLoadingBlocks;
      total = ApiDataRaw.blocks?.length || 0;
      break;
    case OnboardingTabsEnum.users:
      isLoading = ApiDataRaw.isLoadingUsers;
      total = ApiDataRaw.users?.length || 0;
      break;
    default:
      isLoading = false;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: '1rem',
        overflow: 'auto',
        color: theme.colors.common.white,
      }}
    >
      <OnboardListHeader visibleTotal={visibleTotal} total={total} />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          overflowY: 'hidden',
          alignItems: 'flex-start',
          flexGap: '0.5rem',
          pt: '0.5rem',
        }}
      >
        {isLoading ? (
          <LoadingSkeletonGeneric />
        ) : (
          <OnboardVirtualizedList items={listItems} />
        )}
      </Box>
    </Box>
  );
};

export default OnboardList;
